import React, { useEffect } from 'react'
import { Box, CircularProgress, Typography, IconButton } from '@mui/material'
import { PhotoCamera, Cameraswitch } from '@mui/icons-material'
import { useTheme } from '@mui/styles'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useGetOrgImageQuery } from 'services/assets'
import { dispatchSnackbar } from 'utils/helpers'
import { useDispatch } from 'react-redux'

const ScaleImageCell = ({ filename, imageType, isNativeCamera, hasAxleWeights, onRetake, onClick }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation('inboundTicket')
  const { data, isFetching, error } = useGetOrgImageQuery({ filename }, { skip: !filename })

  useEffect(() => {
    if (error?.data?.code === 'NOT_FOUND') {
      dispatchSnackbar(
        dispatch,
        'error',
        'Image was not captured successfully by the camera. Please delete and try capturing again.',
      )
    }
  }, [error])

  const renderCellContent = () => {
    if (!filename && isNativeCamera) {
      return (
        <Typography color="primary" align="center" p={2} sx={{ fontSize: 14, textTransform: 'uppercase' }}>
          <PhotoCamera style={{ verticalAlign: 'bottom' }} /> {t('upload_photo')}
        </Typography>
      )
    }
    if (filename && isFetching) {
      return (
        <Typography align="center" width="100%">
          <CircularProgress />
        </Typography>
      )
    }
    if (filename && (data || !isFetching)) {
      return (
        <>
          {!isNativeCamera && !hasAxleWeights && (
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 1,
                right: 1,
                zIndex: 1,
                border: 1,
                borderColor: 'grey.400',
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: '#EDF7ED',
                },
              }}
              onClick={event => {
                event.stopPropagation()
                onRetake()
              }}
            >
              <Cameraswitch />
            </IconButton>
          )}
          <img src={data} alt="" width="100%" />
        </>
      )
    }
    return (
      <Typography align="center" p={2} sx={{ fontSize: 14, textTransform: 'capitalize' }}>
        {t('image_appears', { type: t(imageType) })}
      </Typography>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        height: 130,
        border: 1,
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        cursor: data || isNativeCamera || hasAxleWeights ? 'pointer' : 'default',
      }}
      onClick={() => {
        onClick()
      }}
      width="100%"
    >
      {renderCellContent()}
    </Box>
  )
}

ScaleImageCell.propTypes = {
  filename: PropTypes.string,
  imageType: PropTypes.string.isRequired,
  isNativeCamera: PropTypes.bool,
  hasAxleWeights: PropTypes.bool,
  onRetake: PropTypes.func,
  onClick: PropTypes.func,
}

ScaleImageCell.defaultProps = {
  filename: undefined,
  isNativeCamera: false,
  hasAxleWeights: false,
  onRetake: () => {},
  onClick: () => {},
}

export default ScaleImageCell
